import { API_BASE } from '../config';
import { clearDeviceToken, DEVICE_HEADER, getDeviceToken } from '../device/deviceToken';
import { clearOperator, getOperatorToken } from '../device/operatorToken';

import type { NetTransport, TransportOutcome } from './engine';

/**
 * fetch-based {@link NetTransport}: one attempt, no policy. Attaches the paired
 * device token (the perimeter) and, when somebody is signed in, the operator
 * bearer (who is working) — owns its own timeout (the engine's UNKNOWN policy
 * depends on a request never hanging forever), and sorts every failure into
 * answered / unknown / failed for the engine.
 *
 * A 401 is sorted by WHAT was refused: the dispatcher's OPERATOR_REQUIRED and
 * the guard's "Sign in again" mean the operator token is missing/expired/
 * mismatched — drop the operator, keep the device paired, the PIN pad comes
 * up. Any other 401 means the DEVICE token was revoked: clear it so the app
 * falls back to the pairing screen. Either way the answer is reported upward.
 */

function apiBase(): string {
  return API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
}

/** 401 bodies that mean "operator", not "device". */
export function isOperatorRefusal(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) {
    return false;
  }
  const error = (body as { error?: { code?: unknown; message?: unknown } }).error;
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  if (error.code === 'OPERATOR_REQUIRED') {
    return true;
  }
  return typeof error.message === 'string' && /^sign in again\b/i.test(error.message);
}

/** Auth headers every floor call carries (device always; operator when signed in). */
export function floorAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { [DEVICE_HEADER]: getDeviceToken() ?? '' };
  const operator = getOperatorToken();
  if (operator !== null) {
    headers['Authorization'] = `Bearer ${operator}`;
  }
  return headers;
}

export class FetchTransport implements NetTransport {
  async post(path: string, body: unknown, timeoutMs: number): Promise<TransportOutcome> {
    const controller = new AbortController();
    const watchdog = window.setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    let response: Response;
    try {
      response = await fetch(`${apiBase()}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...floorAuthHeaders(),
        },
        body: JSON.stringify(body ?? {}),
        signal: controller.signal,
      });
    } catch {
      // Abort/timeout and network errors are indistinguishable from a reply
      // dying on the way back: UNKNOWN, never blindly retried for writes.
      // (True never-sent is caught by the engine's offline check before send.)
      return { kind: 'unknown' };
    } finally {
      window.clearTimeout(watchdog);
    }

    let parsed: unknown = null;
    try {
      const text = await response.text();
      parsed = text === '' ? null : (JSON.parse(text) as unknown);
    } catch {
      // An unreadable body on a reachable server is still an ANSWER.
      parsed = null;
    }

    if (response.status === 401) {
      if (isOperatorRefusal(parsed)) {
        clearOperator();
      } else {
        clearDeviceToken();
      }
    }

    return { kind: 'answered', status: response.status, body: parsed };
  }
}
